<template>
  <div class="payment-settings-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>支付配置管理</h2>
      <p class="page-desc">配置微信支付和支付宝的商户信息、API密钥等参数</p>
    </div>

    <!-- 操作按钮 -->
    <div class="action-bar">
      <el-button type="primary" @click="handleSave" :loading="saving">
        <el-icon><Check /></el-icon>
        保存配置
      </el-button>
      <el-button @click="handleReset">
        <el-icon><RefreshLeft /></el-icon>
        重置
      </el-button>
      <el-button @click="handleTest" :loading="testing">
        <el-icon><Connection /></el-icon>
        测试连接
      </el-button>
    </div>

    <!-- 配置表单 -->
    <el-tabs v-model="activeTab" class="config-tabs">
      <!-- 微信支付配置 -->
      <el-tab-pane label="微信支付" name="wechat">
        <el-card class="config-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span>
                <el-icon color="#09BB07"><Check /></el-icon>
                微信支付配置
              </span>
              <el-tag :type="wechatConfig.enabled ? 'success' : 'info'" size="small">
                {{ wechatConfig.enabled ? '已启用' : '未启用' }}
              </el-tag>
            </div>
          </template>

          <el-form 
            ref="wechatFormRef" 
            :model="wechatConfig" 
            :rules="wechatRules"
            label-width="140px"
            class="payment-form"
          >
            <!-- 启用状态 -->
            <el-form-item label="启用状态">
              <el-switch 
                v-model="wechatConfig.enabled"
                active-text="启用"
                inactive-text="禁用"
              />
            </el-form-item>

            <el-divider content-position="left">基础配置</el-divider>

            <!-- AppID -->
            <el-form-item label="应用ID (AppID)" prop="appId">
              <el-input 
                v-model="wechatConfig.appId" 
                placeholder="请输入微信AppID (如: wx1234567890abcdef)"
                clearable
              >
                <template #prepend>AppID</template>
              </el-input>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                从微信公众平台或小程序后台获取
              </div>
            </el-form-item>

            <!-- 商户号 -->
            <el-form-item label="商户号 (MchID)" prop="mchId">
              <el-input 
                v-model="wechatConfig.mchId" 
                placeholder="请输入商户号 (如: 1234567890)"
                clearable
              >
                <template #prepend>MchID</template>
              </el-input>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                从微信支付商户平台获取
              </div>
            </el-form-item>

            <!-- API密钥 -->
            <el-form-item label="API密钥 (V2)" prop="apiKey">
              <el-input 
                v-model="wechatConfig.apiKey" 
                type="password"
                placeholder="请输入32位API密钥"
                clearable
                show-password
              >
                <template #prepend>API Key</template>
              </el-input>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                从商户平台"账户中心-API安全"设置
              </div>
            </el-form-item>

            <el-divider content-position="left">回调配置</el-divider>

            <!-- 支付回调地址 -->
            <el-form-item label="支付回调地址" prop="notifyUrl">
              <el-input 
                v-model="wechatConfig.notifyUrl" 
                placeholder="https://yourdomain.com/api/payment/callback/wechat"
                clearable
              >
                <template #prepend>
                  <el-icon><Link /></el-icon>
                </template>
                <template #append>
                  <el-button @click="handleCopy(wechatConfig.notifyUrl)">
                    <el-icon><DocumentCopy /></el-icon>
                  </el-button>
                </template>
              </el-input>
              <div class="form-tip warning">
                <el-icon><Warning /></el-icon>
                必须是HTTPS协议且公网可访问的地址
              </div>
            </el-form-item>

            <el-divider content-position="left">高级配置 (可选)</el-divider>

            <!-- API V3密钥 -->
            <el-form-item label="API V3密钥">
              <el-input 
                v-model="wechatConfig.apiV3Key" 
                type="password"
                placeholder="用于微信支付V3版本 (可选)"
                clearable
                show-password
              >
                <template #prepend>V3 Key</template>
              </el-input>
            </el-form-item>

            <!-- 证书序列号 -->
            <el-form-item label="证书序列号">
              <el-input 
                v-model="wechatConfig.serialNo" 
                placeholder="证书序列号 (可选)"
                clearable
              />
            </el-form-item>

            <!-- 证书路径 -->
            <el-form-item label="证书路径">
              <el-input 
                v-model="wechatConfig.certPath" 
                placeholder="./certs/wechat/apiclient_cert.pem"
                clearable
              >
                <template #prepend>Cert</template>
              </el-input>
            </el-form-item>

            <!-- 私钥路径 -->
            <el-form-item label="私钥路径">
              <el-input 
                v-model="wechatConfig.privateKeyPath" 
                placeholder="./certs/wechat/apiclient_key.pem"
                clearable
              >
                <template #prepend>Key</template>
              </el-input>
            </el-form-item>

            <el-divider content-position="left">环境配置</el-divider>

            <!-- 环境选择 -->
            <el-form-item label="支付环境">
              <el-radio-group v-model="wechatConfig.environment">
                <el-radio label="production">生产环境</el-radio>
                <el-radio label="sandbox">沙箱环境</el-radio>
              </el-radio-group>
            </el-form-item>

            <!-- 配置说明 -->
            <el-alert
              title="配置说明"
              type="info"
              :closable="false"
              show-icon
            >
              <template #default>
                <ol style="margin: 0; padding-left: 20px;">
                  <li>登录 <a href="https://pay.weixin.qq.com" target="_blank">微信支付商户平台</a></li>
                  <li>在"账户中心 - API安全"中设置API密钥</li>
                  <li>下载证书文件并上传到服务器指定目录</li>
                  <li>在商户平台配置回调URL白名单</li>
                </ol>
              </template>
            </el-alert>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 支付宝配置 -->
      <el-tab-pane label="支付宝支付" name="alipay">
        <el-card class="config-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span>
                <el-icon color="#1677FF"><Check /></el-icon>
                支付宝配置
              </span>
              <el-tag :type="alipayConfig.enabled ? 'success' : 'info'" size="small">
                {{ alipayConfig.enabled ? '已启用' : '未启用' }}
              </el-tag>
            </div>
          </template>

          <el-form 
            ref="alipayFormRef" 
            :model="alipayConfig" 
            :rules="alipayRules"
            label-width="140px"
            class="payment-form"
          >
            <!-- 启用状态 -->
            <el-form-item label="启用状态">
              <el-switch 
                v-model="alipayConfig.enabled"
                active-text="启用"
                inactive-text="禁用"
              />
            </el-form-item>

            <el-divider content-position="left">基础配置</el-divider>

            <!-- 应用ID -->
            <el-form-item label="应用ID (AppID)" prop="appId">
              <el-input 
                v-model="alipayConfig.appId" 
                placeholder="请输入支付宝AppID (如: 2021001234567890)"
                clearable
              >
                <template #prepend>AppID</template>
              </el-input>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                从支付宝开放平台获取
              </div>
            </el-form-item>

            <!-- 应用私钥 -->
            <el-form-item label="应用私钥" prop="privateKey">
              <el-input 
                v-model="alipayConfig.privateKey" 
                type="textarea"
                :rows="4"
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;应用私钥内容&#10;-----END RSA PRIVATE KEY-----"
                show-word-limit
              />
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                RSA2私钥,包含完整的PEM格式
              </div>
            </el-form-item>

            <!-- 支付宝公钥 -->
            <el-form-item label="支付宝公钥" prop="alipayPublicKey">
              <el-input 
                v-model="alipayConfig.alipayPublicKey" 
                type="textarea"
                :rows="4"
                placeholder="-----BEGIN PUBLIC KEY-----&#10;支付宝公钥内容&#10;-----END PUBLIC KEY-----"
                show-word-limit
              />
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                从支付宝开放平台获取的公钥
              </div>
            </el-form-item>

            <el-divider content-position="left">回调配置</el-divider>

            <!-- 支付回调地址 -->
            <el-form-item label="支付回调地址" prop="notifyUrl">
              <el-input 
                v-model="alipayConfig.notifyUrl" 
                placeholder="https://yourdomain.com/api/payment/callback/alipay"
                clearable
              >
                <template #prepend>
                  <el-icon><Link /></el-icon>
                </template>
                <template #append>
                  <el-button @click="handleCopy(alipayConfig.notifyUrl)">
                    <el-icon><DocumentCopy /></el-icon>
                  </el-button>
                </template>
              </el-input>
              <div class="form-tip warning">
                <el-icon><Warning /></el-icon>
                必须是HTTPS协议且公网可访问的地址
              </div>
            </el-form-item>

            <!-- 支付成功跳转地址 -->
            <el-form-item label="成功跳转地址">
              <el-input 
                v-model="alipayConfig.returnUrl" 
                placeholder="https://yourdomain.com/payment/success"
                clearable
              >
                <template #prepend>
                  <el-icon><Link /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-divider content-position="left">环境配置</el-divider>

            <!-- 网关地址 -->
            <el-form-item label="网关地址" prop="gateway">
              <el-select v-model="alipayConfig.gateway" placeholder="请选择网关">
                <el-option 
                  label="正式环境" 
                  value="https://openapi.alipay.com/gateway.do" 
                />
                <el-option 
                  label="沙箱环境" 
                  value="https://openapi.alipaydev.com/gateway.do" 
                />
              </el-select>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                测试时使用沙箱环境,上线使用正式环境
              </div>
            </el-form-item>

            <!-- 签名类型 -->
            <el-form-item label="签名类型">
              <el-radio-group v-model="alipayConfig.signType">
                <el-radio label="RSA2">RSA2 (推荐)</el-radio>
                <el-radio label="RSA">RSA</el-radio>
              </el-radio-group>
            </el-form-item>

            <!-- 配置说明 -->
            <el-alert
              title="配置说明"
              type="info"
              :closable="false"
              show-icon
            >
              <template #default>
                <ol style="margin: 0; padding-left: 20px;">
                  <li>登录 <a href="https://open.alipay.com" target="_blank">支付宝开放平台</a></li>
                  <li>在"开发者中心 - 网页&移动应用"创建应用</li>
                  <li>生成RSA2密钥对,上传公钥到支付宝平台</li>
                  <li>获取支付宝公钥并配置到本系统</li>
                  <li>在应用详情中配置回调URL</li>
                </ol>
              </template>
            </el-alert>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 通用配置 -->
      <el-tab-pane label="通用配置" name="common">
        <el-card class="config-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span>
                <el-icon><Setting /></el-icon>
                通用支付配置
              </span>
            </div>
          </template>

          <el-form 
            :model="commonConfig" 
            label-width="140px"
            class="payment-form"
          >
            <!-- 订单过期时间 -->
            <el-form-item label="订单过期时间">
              <el-input-number 
                v-model="commonConfig.orderExpireTime" 
                :min="5"
                :max="120"
                :step="5"
              />
              <span style="margin-left: 10px;">分钟</span>
              <div class="form-tip">
                <el-icon><InfoFilled /></el-icon>
                超过此时间未支付的订单将自动关闭
              </div>
            </el-form-item>

            <!-- 支持的支付方式 -->
            <el-form-item label="支持的支付方式">
              <el-checkbox-group v-model="commonConfig.supportedMethods">
                <el-checkbox label="wechat">微信支付</el-checkbox>
                <el-checkbox label="alipay">支付宝</el-checkbox>
              </el-checkbox-group>
            </el-form-item>

            <!-- 最小支付金额 -->
            <el-form-item label="最小支付金额">
              <el-input-number 
                v-model="commonConfig.minAmount" 
                :min="0.01"
                :max="1"
                :step="0.01"
                :precision="2"
              />
              <span style="margin-left: 10px;">元</span>
            </el-form-item>

            <!-- 最大支付金额 -->
            <el-form-item label="最大支付金额">
              <el-input-number 
                v-model="commonConfig.maxAmount" 
                :min="100"
                :max="100000"
                :step="100"
              />
              <span style="margin-left: 10px;">元</span>
            </el-form-item>

            <!-- 货币类型 -->
            <el-form-item label="货币类型">
              <el-select v-model="commonConfig.currency" disabled>
                <el-option label="人民币 (CNY)" value="CNY" />
              </el-select>
            </el-form-item>
          </el-form>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 当前配置状态 -->
    <el-card class="status-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>
            <el-icon><Monitor /></el-icon>
            当前配置状态
          </span>
          <el-button size="small" @click="handleRefreshStatus">
            <el-icon><Refresh /></el-icon>
            刷新状态
          </el-button>
        </div>
      </template>

      <el-row :gutter="20">
        <el-col :span="8">
          <div class="status-item">
            <div class="status-icon" :class="wechatConfig.enabled ? 'success' : 'disabled'">
              <el-icon :size="24"><Check /></el-icon>
            </div>
            <div class="status-content">
              <div class="status-label">微信支付</div>
              <div class="status-value">{{ wechatConfig.enabled ? '已启用' : '未启用' }}</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="status-item">
            <div class="status-icon" :class="alipayConfig.enabled ? 'success' : 'disabled'">
              <el-icon :size="24"><Check /></el-icon>
            </div>
            <div class="status-content">
              <div class="status-label">支付宝</div>
              <div class="status-value">{{ alipayConfig.enabled ? '已启用' : '未启用' }}</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="status-item">
            <div class="status-icon info">
              <el-icon :size="24"><Clock /></el-icon>
            </div>
            <div class="status-content">
              <div class="status-label">最后更新</div>
              <div class="status-value">{{ lastUpdateTime || '未更新' }}</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Check, RefreshLeft, Connection, InfoFilled, Warning, Link, 
  DocumentCopy, Setting, Monitor, Refresh, Clock 
} from '@element-plus/icons-vue'
import axios from 'axios'

export default {
  name: 'PaymentSettings',
  components: {
    Check, RefreshLeft, Connection, InfoFilled, Warning, Link,
    DocumentCopy, Setting, Monitor, Refresh, Clock
  },
  setup() {
    const activeTab = ref('wechat')
    const saving = ref(false)
    const testing = ref(false)
    const lastUpdateTime = ref('')

    // 微信支付配置
    const wechatConfig = reactive({
      enabled: false,
      appId: '',
      mchId: '',
      apiKey: '',
      apiV3Key: '',
      serialNo: '',
      certPath: './certs/wechat/apiclient_cert.pem',
      privateKeyPath: './certs/wechat/apiclient_key.pem',
      notifyUrl: '',
      environment: 'production'
    })

    // 支付宝配置
    const alipayConfig = reactive({
      enabled: false,
      appId: '',
      privateKey: '',
      alipayPublicKey: '',
      signType: 'RSA2',
      gateway: 'https://openapi.alipay.com/gateway.do',
      notifyUrl: '',
      returnUrl: ''
    })

    // 通用配置
    const commonConfig = reactive({
      orderExpireTime: 30,
      supportedMethods: ['wechat', 'alipay'],
      minAmount: 0.01,
      maxAmount: 10000,
      currency: 'CNY'
    })

    // 表单引用
    const wechatFormRef = ref(null)
    const alipayFormRef = ref(null)

    // 微信支付验证规则
    const wechatRules = {
      appId: [
        { required: true, message: '请输入微信AppID', trigger: 'blur' },
        { pattern: /^wx[a-zA-Z0-9]{16}$/, message: 'AppID格式不正确', trigger: 'blur' }
      ],
      mchId: [
        { required: true, message: '请输入商户号', trigger: 'blur' },
        { pattern: /^\d{10}$/, message: '商户号格式不正确', trigger: 'blur' }
      ],
      apiKey: [
        { required: true, message: '请输入API密钥', trigger: 'blur' },
        { min: 32, max: 32, message: 'API密钥必须是32位', trigger: 'blur' }
      ],
      notifyUrl: [
        { required: true, message: '请输入回调地址', trigger: 'blur' },
        { pattern: /^https:\/\//, message: '回调地址必须使用HTTPS协议', trigger: 'blur' }
      ]
    }

    // 支付宝验证规则
    const alipayRules = {
      appId: [
        { required: true, message: '请输入支付宝AppID', trigger: 'blur' },
        { pattern: /^\d{16}$/, message: 'AppID格式不正确', trigger: 'blur' }
      ],
      privateKey: [
        { required: true, message: '请输入应用私钥', trigger: 'blur' }
      ],
      alipayPublicKey: [
        { required: true, message: '请输入支付宝公钥', trigger: 'blur' }
      ],
      notifyUrl: [
        { required: true, message: '请输入回调地址', trigger: 'blur' },
        { pattern: /^https:\/\//, message: '回调地址必须使用HTTPS协议', trigger: 'blur' }
      ],
      gateway: [
        { required: true, message: '请选择网关地址', trigger: 'change' }
      ]
    }

    // 加载配置
    const loadConfig = async () => {
      try {
        const response = await axios.get('/api/admin/payment/config')
        if (response.data.success) {
          const config = response.data.data
          
          // 微信配置
          if (config.wechat) {
            Object.assign(wechatConfig, config.wechat)
          }
          
          // 支付宝配置
          if (config.alipay) {
            Object.assign(alipayConfig, config.alipay)
          }
          
          // 通用配置
          if (config.common) {
            Object.assign(commonConfig, config.common)
          }
          
          lastUpdateTime.value = config.lastUpdateTime || '刚刚'
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        ElMessage.warning('加载配置失败，显示默认配置')
      }
    }

    // 保存配置
    const handleSave = async () => {
      // 验证表单
      let valid = true
      
      if (wechatConfig.enabled && wechatFormRef.value) {
        await wechatFormRef.value.validate((isValid) => {
          valid = valid && isValid
        })
      }
      
      if (alipayConfig.enabled && alipayFormRef.value) {
        await alipayFormRef.value.validate((isValid) => {
          valid = valid && isValid
        })
      }
      
      if (!valid) {
        ElMessage.error('请检查表单填写是否正确')
        return
      }

      try {
        await ElMessageBox.confirm(
          '确定要保存支付配置吗？配置将立即生效。',
          '确认保存',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        saving.value = true
        
        const config = {
          wechat: wechatConfig,
          alipay: alipayConfig,
          common: commonConfig
        }
        
        const response = await axios.post('/api/admin/payment/config', config)
        
        if (response.data.success) {
          ElMessage.success('配置保存成功')
          lastUpdateTime.value = new Date().toLocaleString('zh-CN')
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('保存配置失败:', error)
          ElMessage.error('保存配置失败')
        }
      } finally {
        saving.value = false
      }
    }

    // 重置配置
    const handleReset = () => {
      ElMessageBox.confirm(
        '确定要重置所有配置吗？未保存的修改将丢失。',
        '确认重置',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        loadConfig()
        ElMessage.success('配置已重置')
      }).catch(() => {})
    }

    // 测试连接
    const handleTest = async () => {
      testing.value = true
      try {
        const response = await axios.post('/api/admin/payment/test', {
          wechat: wechatConfig.enabled,
          alipay: alipayConfig.enabled
        })
        
        if (response.data.success) {
          const result = response.data.data
          let message = '测试结果:\n'
          
          if (result.wechat) {
            message += `微信支付: ${result.wechat.success ? '✓ 连接成功' : '✗ 连接失败'}\n`
          }
          if (result.alipay) {
            message += `支付宝: ${result.alipay.success ? '✓ 连接成功' : '✗ 连接失败'}`
          }
          
          ElMessageBox.alert(message, '测试结果', {
            confirmButtonText: '确定'
          })
        }
      } catch (error) {
        console.error('测试失败:', error)
        ElMessage.error('连接测试失败')
      } finally {
        testing.value = false
      }
    }

    // 复制到剪贴板
    const handleCopy = (text) => {
      if (!text) {
        ElMessage.warning('内容为空，无法复制')
        return
      }
      
      navigator.clipboard.writeText(text).then(() => {
        ElMessage.success('已复制到剪贴板')
      }).catch(() => {
        ElMessage.error('复制失败')
      })
    }

    // 刷新状态
    const handleRefreshStatus = () => {
      loadConfig()
      ElMessage.success('状态已刷新')
    }

    // 页面加载时获取配置
    onMounted(() => {
      loadConfig()
    })

    return {
      activeTab,
      saving,
      testing,
      lastUpdateTime,
      wechatConfig,
      alipayConfig,
      commonConfig,
      wechatFormRef,
      alipayFormRef,
      wechatRules,
      alipayRules,
      handleSave,
      handleReset,
      handleTest,
      handleCopy,
      handleRefreshStatus
    }
  }
}
</script>

<style scoped>
.payment-settings-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.page-desc {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.action-bar {
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.config-tabs {
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.config-card {
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.card-header span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-form {
  margin-top: 20px;
}

.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-tip.warning {
  color: #E6A23C;
}

.form-tip a {
  color: #409EFF;
  text-decoration: none;
}

.form-tip a:hover {
  text-decoration: underline;
}

.status-card {
  margin-top: 20px;
  border: none;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  transition: all 0.3s;
}

.status-item:hover {
  background: #ecf5ff;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.status-icon.success {
  background: #67C23A;
}

.status-icon.disabled {
  background: #909399;
}

.status-icon.info {
  background: #409EFF;
}

.status-content {
  flex: 1;
}

.status-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.status-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

:deep(.el-divider__text) {
  font-weight: 600;
  color: #606266;
}

:deep(.el-alert) {
  margin-top: 20px;
}

:deep(.el-alert ol) {
  line-height: 1.8;
}
</style>
